import { OrderSiteAvailability } from '@moko/ui';
import {
  siteAvailabilityRequests,
  type SiteAvailabilityDraft,
} from '@moko/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IMetadata,
  getMetadata,
  getEntitiesMetadatas,
} from '../../services/metadataService';
import { validate, type IValidationError } from '@moko/utils';
import {
  ICreateOrderRequest,
  IUpdateOrderRequest,
  createOrderedEntity,
  createOrder,
  editOrder,
  getOrder,
} from '../../services/orderService';
import {
  getOrderStatuses,
  type IOrderStatus,
} from '../../services/workService';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { useLocation } from 'react-router-dom';
import OtherInfo from '../../common/OtherInfoPortal';
import { EditItem, WorkTypeField, useTenantWorkTypes } from '@moko/ui';
import {
  FontIcon,
  IButtonProps,
  Pivot,
  PivotItem,
  TooltipHost,
} from '@fluentui/react';
import OrderAssets from './OrderAssets';
import {
  hasCodeConventionFormat,
  tenantInfoAtom,
} from '../../atoms/tenantAtom';
import { getTenantWorkTypes } from '../../services/settingsService';

const Order = () => {
  const [siteAvailabilityWindows, setSiteAvailabilityWindows] = useState<
    SiteAvailabilityDraft[]
  >([]);
  const query = new URLSearchParams(useLocation().search);
  const routeOrderId = Number.parseInt(query.get('id') ?? '');

  const [metadata, setMetadata] = useState<IMetadata>();
  const [currentOrderId, setCurrentOrderId] = useState<number | undefined>(
    Number.isNaN(routeOrderId) ? undefined : routeOrderId
  );
  const initialRelatedAssetIdsRef = useRef<number[]>([]);
  const [relatedAssetIds, setRelatedAssetIds] = useState<number[]>([]);
  const [allEntitiesMetadatas, setAllEntitiesMetadatas] = useState<
    { key: string; value: string }[]
  >([]);
  const [orderedEntitiesMetadatas, setOrderedEntitiesMetadatas] = useState<
    { key: string; value: string }[]
  >([]);
  const [metadatas, setMetadatas] = useState<{ key: string; value: string }[]>(
    []
  );
  const [metadataName, setMetadataName] = useState<string>('');
  const [otherInfo, setOtherInfo] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<IValidationError[]>();
  const companyInfoErrors = useMemo(
    () => validate(otherInfo, metadata?.validations ?? [], metadata?.fields),
    [otherInfo, metadata]
  );
  useEffect(() => {
    setErrors(companyInfoErrors);
  }, [companyInfoErrors]);
  const [orderStatuses, setOrderStatuses] = useState<IOrderStatus[]>([]);
  const [orderedEntityType, setOrderedEntityType] = useState('');
  const [workTypeId, setWorkTypeId] = useState<number | null>(null);
  const [savedWorkTypeId, setSavedWorkTypeId] = useState<number | null>(null);
  const [workTypeReady, setWorkTypeReady] = useState(
    Number.isNaN(routeOrderId)
  );
  const workTypes = useTenantWorkTypes(undefined, true, getTenantWorkTypes);

  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const isSaved = useRef(false);
  const hasDraftRelatedAssetsChanges =
    !currentOrderId &&
    relatedAssetIds.join(',') !== initialRelatedAssetIdsRef.current.join(',');
  const isAssetsTabDisabled = !currentOrderId;
  const assetsTabTooltip = 'Assets can be linked after the order is saved.';
  const disabledAssetsTabProps: IButtonProps | undefined = isAssetsTabDisabled
    ? {
        disabled: true,
        style: { color: 'grey' },
      }
    : undefined;

  const filterOrderedMetadatas = (
    items: { key: string; value: string }[],
    entityType?: string | null
  ) => {
    if (!entityType) {
      return [];
    }
    const prefix = `${entityType}|`.toLowerCase();
    return items.filter((em) => em.key.toLowerCase().startsWith(prefix));
  };

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const entityMetadatas = await getEntitiesMetadatas(abortController, [
        'Work',
        'Asset',
        'Order',
      ]);
      const allEntitiesMetadatas = Object.keys(
        entityMetadatas?.metadatas || {}
      ).map((key) => ({ key: key, value: key.split('|')[1] }));
      setAllEntitiesMetadatas(allEntitiesMetadatas);
      const metadatas =
        allEntitiesMetadatas?.filter((em) => em.key.startsWith('Order|')) ?? [];
      setMetadatas(metadatas);
      setOrderStatuses(await getOrderStatuses(abortController));
      if (routeOrderId) {
        const order: any = await getOrder(abortController, routeOrderId);
        setOrderedEntitiesMetadatas(
          filterOrderedMetadatas(
            allEntitiesMetadatas ?? [],
            order.entityType ?? order.orderedEntityType
          )
        );
      }
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tenantInfo = useAtomValue(tenantInfoAtom);
  const hasCodeConvention = hasCodeConventionFormat(
    tenantInfo,
    'order-code-convention'
  );
  const isEditMode = !!currentOrderId;

  return (
    <>
      <Pivot>
        <PivotItem headerText="Details">
          <EditItem
            headerSlot={
              (isEditMode || orderedEntityType === 'Work') && (
                <WorkTypeField
                  mode={isEditMode ? 'read' : 'create'}
                  options={workTypes.options}
                  value={isEditMode ? savedWorkTypeId : workTypeId}
                  onChange={setWorkTypeId}
                  loading={workTypes.loading || (isEditMode && !workTypeReady)}
                  error={workTypes.error ? 'Could not load work types.' : null}
                  onRetry={workTypes.refetch}
                />
              )
            }
            getAction={async (abortController, id) => {
              const order: any = await getOrder(abortController, id);
              setCurrentOrderId(id);
              const ids = (order.relatedAssets ?? []).map((a: any) => a.id);
              setRelatedAssetIds(ids);
              initialRelatedAssetIdsRef.current = ids;
              setOtherInfo(order.otherInfo);
              order.statusId = (order.statusId ?? '').toString();
              const metadataName = order.metadataName
                ? order.metadataName.split('|')[1]
                : undefined;
              setMetadataName(metadataName);
              const metadata = await getMetadata(
                abortController,
                'Order',
                undefined,
                metadataName
              );
              setMetadata(metadata);
              setSavedWorkTypeId(
                typeof order.workTypeId === 'number' ? order.workTypeId : null
              );
              setOrderedEntityType(
                order.orderedEntityType ?? order.entityType ?? ''
              );
              setWorkTypeReady(true);
              return order;
            }}
            saveDisabled={companyInfoErrors.length > 0}
            newAction={async (abortController, newOrder) => {
              const otherInfoErrors = validate(
                otherInfo,
                metadata?.validations ?? [],
                metadata?.fields
              );
              if (otherInfoErrors.length > 0) {
                setErrors(otherInfoErrors);
                return;
              }
              const data: ICreateOrderRequest = {
                siteAvailabilityWindows: siteAvailabilityRequests(
                  siteAvailabilityWindows
                ),
                description: newOrder.description,
                code: newOrder.code,
                statusId: newOrder.statusId
                  ? Number.parseInt(newOrder.statusId)
                  : undefined,
                metadataName: `Order|${metadataName}`,
                orderedEntityType: newOrder.orderedEntityType,
                orderedMetadataName: newOrder.orderedMetadataName,
                relatedAssetIds: relatedAssetIds,
                otherInfo,
                ...(newOrder.orderedEntityType === 'Work'
                  ? { workTypeId }
                  : {}),
              };
              try {
                const newOrderResponse = await createOrder(
                  abortController,
                  data
                );
                setCurrentOrderId(newOrderResponse.orderId);
                initialRelatedAssetIdsRef.current = relatedAssetIds;
                setSuccessMessage(
                  `Order with id: ${newOrderResponse.orderId} created.`
                );
              } catch (error) {
                setErrorMessage(
                  (error as { message?: string })?.message ||
                    'Create order failed.'
                );
                throw error;
              }
            }}
            editAction={async (abortController, id, editedOrder) => {
              const otherInfoErrors = validate(
                otherInfo,
                metadata?.validations ?? [],
                metadata?.fields
              );
              if (otherInfoErrors.length > 0) {
                setErrors(otherInfoErrors);
                return;
              }
              const data: IUpdateOrderRequest = {
                code: editedOrder.code,
                description: editedOrder.description,
                orderedEntityType: editedOrder.orderedEntityType,
                orderedMetadataName: editedOrder.orderedMetadataName,
                relatedAssetIds: relatedAssetIds,
                otherInfo,
                statusId: editedOrder.statusId
                  ? Number.parseInt(editedOrder.statusId)
                  : undefined,
              };
              await editOrder(abortController, id, data);
              setSuccessMessage(`Order with id: ${id} successfully updated.`);
            }}
            isSaved={isSaved}
            isDirtyExternal={
              hasDraftRelatedAssetsChanges ||
              (!currentOrderId && siteAvailabilityWindows.length > 0) ||
              (!currentOrderId && workTypeId != null)
            }
            back={'/orders'}
            postButtons={[
              {
                text: 'Create related entity',
                icon: { iconName: 'AddTo' },
                onClick: async () => {
                  if (!currentOrderId) {
                    setErrorMessage(
                      'Save Order before creating related entity.'
                    );
                    return;
                  }
                  const abortController = new AbortController();
                  try {
                    const result = await createOrderedEntity(
                      abortController,
                      currentOrderId
                    );
                    setSuccessMessage(
                      `Created related entity with id: ${result.entityId} of type ${result.entityType}.`
                    );
                  } catch (error) {
                    setErrorMessage(
                      (error as { message?: string })?.message ||
                        'Could not create the related entity.'
                    );
                  }
                },
              },
            ]}
            afterChange={async (newValue: any) => {
              const entity =
                newValue.orderedEntityType ?? newValue.entityType ?? '';
              setOrderedEntityType(entity);
              if (!currentOrderId && entity !== 'Work') setWorkTypeId(null);
              setOrderedEntitiesMetadatas(
                filterOrderedMetadatas(allEntitiesMetadatas ?? [], entity)
              );
              if (newValue.hasOwnProperty('metadataName')) {
                const metadataName = newValue.metadataName.split('|')[1];
                setMetadataName(metadataName);
                const abortController = new AbortController();
                const metadata = await getMetadata(
                  abortController,
                  'Order',
                  undefined,
                  metadataName
                );
                setMetadata(metadata);
              }
            }}
            metadata={{
              fields: [
                ...(hasCodeConvention && !isEditMode
                  ? []
                  : [
                      {
                        name: 'code',
                        fieldType: 'String',
                        label: 'Code',
                        readonly: hasCodeConvention && isEditMode,
                      },
                    ]),
                {
                  name: 'description',
                  fieldType: 'String',
                  label: 'Description',
                },
                {
                  name: 'statusId',
                  fieldType: 'Lookup',
                  label: 'Status',
                  lookupList: 'Statuses',
                },
                {
                  name: 'metadataName',
                  fieldType: 'Lookup',
                  label: 'Order form',
                  lookupList: 'metadatas',
                  effect: currentOrderId ? 'Hide' : 'Show',
                },
                {
                  name: 'orderedEntityType',
                  fieldType: 'Lookup',
                  label: 'Order entity',
                  lookupList: 'entityTypes',
                },
                {
                  name: 'orderedMetadataName',
                  fieldType: 'Lookup',
                  label: 'Work/Asset form',
                  lookupList: 'orderedEntitiesMetadatas',
                },
                {
                  name: 'isTemplate',
                  fieldType: 'Checkbox',
                  label: 'Is Template',
                },
              ],
              lookups: [
                {
                  name: 'Statuses',
                  values: orderStatuses.map((s) => ({
                    key: s.id.toString(),
                    value: s.name,
                  })),
                },
                {
                  name: 'metadatas',
                  values: metadatas,
                },
                {
                  name: 'entityTypes',
                  values: [
                    { key: 'Work', value: 'Work' },
                    { key: 'Asset', value: 'Asset' },
                  ],
                },
                {
                  name: 'orderedEntitiesMetadatas',
                  values: orderedEntitiesMetadatas,
                },
              ],
              validations: [],
              interactions: [],
            }}
          >
            <>
              <OrderSiteAvailability
                key={currentOrderId ?? 'new'}
                orderId={currentOrderId || undefined}
                value={siteAvailabilityWindows}
                onChange={setSiteAvailabilityWindows}
                source="Portal"
              />
              {metadata ? (
                <OtherInfo
                  metadata={metadata}
                  otherInfo={JSON.stringify(otherInfo)}
                  isSaved={isSaved}
                  onOtherInfoChanged={(newValue: any) => setOtherInfo(newValue)}
                  validationErrors={errors}
                  onValidationErrorsChanged={(newErrors) =>
                    setErrors(newErrors)
                  }
                />
              ) : (
                <></>
              )}
            </>
          </EditItem>
        </PivotItem>
        <PivotItem
          headerText="Assets"
          headerButtonProps={disabledAssetsTabProps}
          onRenderItemLink={
            isAssetsTabDisabled
              ? (link, defaultRenderer) => (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {defaultRenderer && link
                      ? defaultRenderer(link)
                      : link?.headerText}
                    <TooltipHost content={assetsTabTooltip}>
                      <FontIcon iconName="Info" style={{ color: '#605e5c' }} />
                    </TooltipHost>
                  </span>
                )
              : undefined
          }
        >
          <OrderAssets
            orderId={currentOrderId}
            assetIds={relatedAssetIds}
            onAssetIdsChanged={setRelatedAssetIds}
          />
        </PivotItem>
      </Pivot>
    </>
  );
};

export default Order;
